import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import Autoplay from "embla-carousel-autoplay";
import {
  HeartPulse,
  ArrowRight,
  ArrowLeft,
  Check,
  GraduationCap,
  ShieldCheck,
  Activity,
  Microscope,
  Headset,
  Stethoscope,
  Utensils,
  MessageSquareWarning,
} from "lucide-react";
import { Carousel, CarouselContent, CarouselItem } from "@/components/ui/carousel";
import { BrandLogo } from "@/components/BrandLogo";
import heroVet from "@/assets/landing/hero-vet.jpg";
import instruments from "@/assets/landing/instruments.jpg";

// SINGLE marketing page for veterinary nurses. Reduced from the previous
// multi-page marketing site (Landing / Simulators / Cert IV Alignment /
// For Individuals / For Clinics / For TAFEs / About / Contact / Pricing).
// All those pages are preserved in /app/frontend/src/pages/_backup_marketing/
// and can be restored by copying the .bak files back and re-adding routes
// to App.js. Deliberately no references to clinics or TAFEs — audience is
// individual vet nurses. Deliberately no subscription CTA — the sim suite
// is currently free for individuals (SUBSCRIPTIONS_ENABLED flag off).

const HUB = "/sections";
const heading = { fontFamily: "'Manrope', ui-sans-serif, system-ui, sans-serif" };

const SECTION_IMG = {
  triage: "https://static.prod-images.emergentagent.com/jobs/71abba4a-702d-4e02-b451-e9e11020a1be/images/0131b06f167bce10e3a297eb809e8090c2c7fd83edd78b5aeda0697786662ed5.png",
  surgery: "https://static.prod-images.emergentagent.com/jobs/71abba4a-702d-4e02-b451-e9e11020a1be/images/f021451c5b395053c780835b0c964349238fc84de63bd466f30360eacda20c92.png",
  pathology: "https://static.prod-images.emergentagent.com/jobs/71abba4a-702d-4e02-b451-e9e11020a1be/images/1a46fcdad019800a2668c1513ef9abc587a4e5cdcd29b65af17f0e311ae63d32.png",
  inpatient: "https://static.prod-images.emergentagent.com/jobs/71abba4a-702d-4e02-b451-e9e11020a1be/images/955fe05fd9dfb4d36f95fbf4aa8d869e1a7192819464ceee531418f01feb8e26.png",
  nutrition: "https://static.prod-images.emergentagent.com/jobs/71abba4a-702d-4e02-b451-e9e11020a1be/images/cdb664161a8bdffc4c454cf7f1d321bd25935589474d9372b0bdf1d9ceba672e.png",
  communication: "https://static.prod-images.emergentagent.com/jobs/71abba4a-702d-4e02-b451-e9e11020a1be/images/2684c6c7ccf8a678dfa17506bf89e01f190591b7dc232eaf9af28e5af253b27c.png",
};

const SECTIONS = [
  { id: "triage-admission", title: "Triage and Admission", count: 3, img: SECTION_IMG.triage, Icon: Headset, desc: "Take incoming calls, recognise red flags and assign the right urgency — front-line phone-triage and admission skills." },
  { id: "surgery-anaesthesia", title: "Surgery and Anaesthesia", count: 8, img: SECTION_IMG.surgery, Icon: Activity, desc: "Plan and monitor anaesthesia, deliver IPPV, calculate drug volumes, chart dental findings and prepare a sterile theatre." },
  { id: "clinical-pathology", title: "Clinical Pathology", count: 3, img: SECTION_IMG.pathology, Icon: Microscope, desc: "Sample handling, haematology, biochemistry and diagnostic interpretation from bench to report." },
  { id: "inpatient-care", title: "Inpatient Care", count: 5, img: SECTION_IMG.inpatient, Icon: Stethoscope, desc: "Fluid therapy, medication rounds, dispensing and infection control for the hospitalised patient." },
  { id: "nutrition-wellbeing", title: "Nutrition and Wellbeing", count: 1, img: SECTION_IMG.nutrition, Icon: Utensils, desc: "Body condition, energy requirements and feeding plans across life stages and clinical needs." },
  { id: "communication-practice", title: "Communication and Professional Practice", count: 3, img: SECTION_IMG.communication, Icon: MessageSquareWarning, desc: "De-escalating difficult conversations, supporting grieving owners and running a professional front desk." },
];

const WHY = [
  { Icon: GraduationCap, title: "Mapped to your qualification", desc: "Scenarios reflect the skills and knowledge required for Cert IV Veterinary Nursing units." },
  { Icon: ShieldCheck, title: "Safe place to fail", desc: "Make the mistake here. Learn the reasoning. Show up to placement already past the rookie errors." },
  { Icon: HeartPulse, title: "Built by clinicians", desc: "Designed with practising vet nurses so the steps, language and judgement calls feel right." },
];

const STATS = [
  ["20+", "Clinical Scenarios"],
  ["100%", "Free to use"],
  ["Cert IV", "Aligned to ACM40418"],
  ["24/7", "Practise Anywhere"],
];

export default function Landing() {
  const [api, setApi] = useState(null);
  const autoplay = useRef(
    Autoplay({ delay: 3800, stopOnInteraction: false, stopOnMouseEnter: true })
  );
  const nudge = (dir) => {
    if (dir === "prev") api?.scrollPrev();
    else api?.scrollNext();
    autoplay.current?.reset();
  };

  return (
    <div
      className="min-h-screen bg-[#0b1015] text-slate-100 antialiased selection:bg-[#2dd4d8]/30"
      style={{ fontFamily: "'Inter', ui-sans-serif, system-ui, sans-serif" }}
      data-testid="landing-page"
    >
      {/* ── Minimal nav: logo + Launch button only ──────────────────────── */}
      <header className="sticky top-0 z-40 border-b border-white/10 bg-[#0b1015]/80 backdrop-blur-md">
        <nav className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <a
            href="#top"
            data-testid="landing-logo"
            className="transition-opacity hover:opacity-80"
            onClick={(e) => {
              e.preventDefault();
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
          >
            <BrandLogo />
          </a>
          <Link
            to={HUB}
            data-testid="nav-launch-btn"
            className="inline-flex items-center gap-1.5 rounded-full bg-[#2dd4d8] px-4 py-2 text-sm font-semibold text-[#06262a] hover:bg-[#5fe2e6] transition-colors"
          >
            Launch Sims <ArrowRight className="w-4 h-4" />
          </Link>
        </nav>
      </header>

      {/* ── Hero ────────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden">
        <div className="pointer-events-none absolute -top-40 -left-40 w-[36rem] h-[36rem] rounded-full bg-[#2dd4d8]/10 blur-3xl" />
        <div className="pointer-events-none absolute top-20 right-0 w-[28rem] h-[28rem] rounded-full bg-[#1e6b8c]/10 blur-3xl" />
        <div className="relative max-w-6xl mx-auto px-6 py-20 lg:py-28 grid lg:grid-cols-2 gap-14 items-center">
          <div className="fade-up">
            <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-3.5 py-1.5 text-xs font-medium text-slate-300">
              <ShieldCheck className="w-3.5 h-3.5 text-[#2dd4d8]" /> Built for veterinary nurses
            </span>
            <h1 className="mt-6 text-5xl sm:text-6xl lg:text-7xl font-extrabold leading-[1.02] tracking-tight" style={heading}>
              Clinical confidence,<br />
              <span className="text-[#2dd4d8]">simulated.</span>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-slate-400 max-w-xl leading-relaxed">
              CertIVSims gives veterinary nursing students and early-career nurses a polished,
              browser-based environment to practise the procedures that matter — before the
              patient is on the table. Free to use, no subscription required.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to={HUB}
                data-testid="hero-open-sims-btn"
                className="inline-flex items-center gap-2 rounded-full bg-[#2dd4d8] px-6 py-3 text-sm font-semibold text-[#06262a] hover:bg-[#5fe2e6] transition-colors"
              >
                Open the Simulators <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href="#simulators"
                data-testid="hero-browse-btn"
                className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.03] px-6 py-3 text-sm font-semibold text-slate-100 hover:bg-white/[0.07] transition-colors"
              >
                Browse scenarios
              </a>
            </div>
            <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-400">
              {["No install", "No credit card", "Works on tablet"].map((t) => (
                <span key={t} className="inline-flex items-center gap-1.5">
                  <Check className="w-4 h-4 text-[#2dd4d8]" /> {t}
                </span>
              ))}
            </div>
          </div>

          {/* hero image card */}
          <div className="relative fade-up" style={{ animationDelay: "120ms" }}>
            <div className="relative rounded-2xl overflow-hidden border border-white/10 shadow-2xl shadow-black/40">
              <img src={heroVet} alt="Veterinary nurse examining a dog with a stethoscope" className="w-full h-[420px] object-cover" />
              <div className="absolute inset-x-4 bottom-4 rounded-xl border border-white/10 bg-[#0b1015]/80 backdrop-blur-md px-4 py-3 flex items-center gap-3">
                <span className="w-9 h-9 rounded-lg bg-[#2dd4d8]/15 flex items-center justify-center shrink-0">
                  <Activity className="w-4.5 h-4.5 text-[#2dd4d8]" />
                </span>
                <div className="flex-1">
                  <div className="text-sm font-semibold text-slate-100">Live clinical scenarios</div>
                  <div className="text-xs text-slate-400">Realistic, assessable, repeatable</div>
                </div>
                <span className="w-2.5 h-2.5 rounded-full bg-[#2dd4d8] animate-pulse" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Stats ───────────────────────────────────────────────────────── */}
      <section className="border-y border-white/10 bg-white/[0.015]">
        <div className="max-w-6xl mx-auto px-6 py-10 grid grid-cols-2 lg:grid-cols-4 gap-8">
          {STATS.map(([n, l]) => (
            <div key={l} className="text-center">
              <div className="text-3xl sm:text-4xl font-extrabold text-[#2dd4d8]" style={heading}>{n}</div>
              <div className="mt-1.5 text-[11px] uppercase tracking-widest text-slate-500 font-semibold">{l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Sim library (sections carousel) ─────────────────────────────── */}
      <section id="simulators" className="max-w-6xl mx-auto px-6 py-20 lg:py-24 scroll-mt-20">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[#2dd4d8]">The Sim Library</div>
            <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight max-w-2xl" style={heading}>
              Six clinical areas, <span className="text-[#2dd4d8]">one suite.</span>
            </h2>
            <p className="mt-4 text-slate-400 max-w-2xl leading-relaxed">
              Every simulator lives inside one of these six clinical sections. Swipe through and
              jump straight in — no sign-up required.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => nudge("prev")} data-testid="carousel-prev" aria-label="Previous section"
              className="w-11 h-11 rounded-full border border-white/15 bg-white/[0.04] text-slate-200 hover:bg-white/[0.1] hover:border-[#2dd4d8]/40 transition-colors flex items-center justify-center">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <button onClick={() => nudge("next")} data-testid="carousel-next" aria-label="Next section"
              className="w-11 h-11 rounded-full border border-white/15 bg-white/[0.04] text-slate-200 hover:bg-white/[0.1] hover:border-[#2dd4d8]/40 transition-colors flex items-center justify-center">
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>

        <Carousel setApi={setApi} opts={{ align: "start", loop: true }} plugins={[autoplay.current]} className="mt-12" data-testid="sections-carousel">
          <CarouselContent className="-ml-5">
            {SECTIONS.map(({ id, title, desc, count, img, Icon }) => (
              <CarouselItem key={id} className="pl-5 basis-[86%] sm:basis-[55%] lg:basis-[42%]">
                <Link
                  to={`/section/${id}`}
                  data-testid={`section-card-${id}`}
                  className="group h-full flex flex-col rounded-2xl overflow-hidden border border-white/10 bg-white/[0.02] hover:border-[#2dd4d8]/40 transition-colors"
                >
                  <div className="relative h-56 overflow-hidden shrink-0">
                    <img src={img} alt={title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0b1015] via-[#0b1015]/30 to-transparent" />
                    <span className="absolute top-4 left-4 inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-[#0b1015]/70 backdrop-blur px-2.5 py-1 text-[11px] font-semibold text-slate-200">
                      <Icon className="w-3.5 h-3.5 text-[#2dd4d8]" /> {count} {count === 1 ? "simulator" : "simulators"}
                    </span>
                  </div>
                  <div className="p-5 flex flex-col flex-1">
                    <div className="flex items-start justify-between gap-3">
                      <h3 className="text-lg font-bold text-slate-100 leading-snug" style={heading}>{title}</h3>
                      <ArrowRight className="w-5 h-5 mt-0.5 text-slate-500 group-hover:text-[#2dd4d8] group-hover:translate-x-1 transition-all shrink-0" />
                    </div>
                    <p className="mt-2 text-sm text-slate-400 leading-relaxed line-clamp-2">{desc}</p>
                  </div>
                </Link>
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>
      </section>

      {/* ── Why ─────────────────────────────────────────────────────────── */}
      <section id="why" className="border-t border-white/10 bg-white/[0.015] scroll-mt-20">
        <div className="max-w-6xl mx-auto px-6 py-20 lg:py-24 grid lg:grid-cols-2 gap-14 items-center">
          <div className="rounded-2xl overflow-hidden border border-white/10 order-2 lg:order-1">
            <img src={instruments} alt="Polished veterinary surgical instruments on a dark tray" className="w-full h-[400px] object-cover" />
          </div>
          <div className="order-1 lg:order-2">
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[#2dd4d8]">Why CertIVSims</div>
            <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight" style={heading}>
              A serious tool for <span className="text-[#2dd4d8]">serious students.</span>
            </h2>
            <div className="mt-8 space-y-6">
              {WHY.map(({ Icon, title, desc }) => (
                <div key={title} className="flex gap-4">
                  <span className="w-10 h-10 rounded-xl bg-[#2dd4d8]/12 ring-1 ring-[#2dd4d8]/30 flex items-center justify-center shrink-0">
                    <Icon className="w-5 h-5 text-[#2dd4d8]" />
                  </span>
                  <div>
                    <h3 className="text-base font-bold text-slate-100" style={heading}>{title}</h3>
                    <p className="mt-1 text-sm text-slate-400 leading-relaxed">{desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA ─────────────────────────────────────────────────────────── */}
      <section id="get-started" className="max-w-6xl mx-auto px-6 py-20 lg:py-24 scroll-mt-20">
        <div className="relative overflow-hidden rounded-3xl border border-white/10 px-8 py-16 text-center"
          style={{ background: "radial-gradient(120% 140% at 50% 0%, rgba(45,212,216,0.18) 0%, rgba(11,16,21,0) 55%), #0e151c" }}>
          <h2 className="text-4xl sm:text-5xl font-extrabold tracking-tight" style={heading}>Ready when you are.</h2>
          <p className="mt-4 text-slate-400 max-w-xl mx-auto leading-relaxed">
            Jump straight into the simulator suite and start building the muscle memory your future
            patients will thank you for.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link
              to={HUB}
              data-testid="cta-launch-btn"
              className="inline-flex items-center gap-2 rounded-full bg-[#2dd4d8] px-7 py-3.5 text-sm font-semibold text-[#06262a] hover:bg-[#5fe2e6] transition-colors"
            >
              Launch CertIVSims <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* ── Footer ──────────────────────────────────────────────────────── */}
      <footer className="border-t border-white/10">
        <div className="max-w-6xl mx-auto px-6 py-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2.5 text-sm text-slate-400">
            <BrandLogo />
            <span className="hidden sm:inline text-slate-600">·</span>
            <span className="hidden sm:inline">Simulators for veterinary nurses</span>
          </div>
          <div className="text-xs text-slate-500">© 2026 CertIVSims · certivsims.com.au</div>
        </div>
      </footer>
    </div>
  );
}
